import React, { useEffect, useRef, useState } from "react";

/**
 * CurrencyInput — controlled money input with the same UX rules as NumberInput.
 * Displays with thousands separators (Indian formatting) and an optional ₹ prefix.
 * Emits a plain `number` (never a string). Empty while editing; blur → 0.
 *
 * Props: value (number), onChange, prefix ("₹"), placeholder, testid, min=0, max=Infinity,
 *        decimals=2, className
 */
export default function CurrencyInput({
  value,
  onChange,
  prefix = "₹",
  placeholder = "0",
  testid,
  disabled,
  min = 0,
  max = Infinity,
  decimals = 2,
  className = "",
}) {
  const format = (n) => {
    if (n === undefined || n === null || n === "" || Number.isNaN(Number(n))) return "";
    const num = Number(n);
    return num.toLocaleString("en-IN", {
      maximumFractionDigits: decimals,
      minimumFractionDigits: 0,
    });
  };
  const [text, setText] = useState(format(value));
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (focused) return;
    setText(format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);

  const parse = (t) => {
    if (t === "" || t === "." || t === "-") return null;
    const clean = String(t).replace(/,/g, "");
    const n = parseFloat(clean);
    return Number.isNaN(n) ? null : n;
  };

  const handleChange = (e) => {
    let v = e.target.value;
    // Strip everything except digits and dot; also strip any commas the browser may not
    v = v.replace(/[^0-9.]/g, "");
    // Only one dot
    const parts = v.split(".");
    if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
    // No leading zero unless "0." pattern
    v = v.replace(/^0+(\d)/, "$1");
    setText(v);
    if (v === "" || v === ".") return; // don't emit while intermediate
    const n = parse(v);
    if (n !== null) {
      const clamped = Math.min(max, Math.max(min, n));
      onChange(clamped);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    const n = parse(text);
    const final = n === null ? 0 : Math.min(max, Math.max(min, n));
    onChange(final);
    setText(format(final));
  };

  return (
    <div className={`relative inline-flex items-center w-full ${className}`}>
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{prefix}</span>
      )}
      <input
        ref={inputRef}
        data-testid={testid}
        type="text"
        inputMode="decimal"
        value={text}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full border border-border rounded ${prefix ? "pl-8" : "pl-3"} pr-3 py-2 text-sm text-right font-mono focus:border-emerald-500 focus:outline-none`}
      />
    </div>
  );
}
